// Copyright (c) Docugami, Inc. All rights reserved.

import { IconButton, mergeStyles, Spinner } from "@fluentui/react";
import { primaryTheme, secondaryTheme } from "components/shell/presentation/AppStyles";
import { dgIconNames } from "hooks/dgIcons";
import { useAppSelector } from "hooks/redux";
import { useMemo, useState } from "react";
import { useLocation } from "react-router";
import { Sample, SampleStatus } from "store/clusters/samplesApi";
import { createSelectGetSamplesState, selectAllSamples, useGetCatalogSamplesQuery, useGetOrganizationSamplesQuery, useInstallSampleMutation } from "store/clusters/samplesSlice";
import { InstalledSampleSetSchema, TelemetryEvent } from "telemetry/TelemetryEvents";
import { Telemetry } from "telemetry/TelemetryManager";
import { groupBy, GroupCollection } from "utils/ArrayUtils";
import { AutomationId } from "utils/Automation";
import { Badge } from "./Badge";
import { Catalog, ICatalogItem } from "./catalog/Catalog";
import { PreviewText } from "./catalog/CatalogPreview";

enum CatalogGroupType {
    Installed = "Installed",
    Recommended = "Recommended"
}

const getCatalogGroupTypeForSampleStatus = (status: SampleStatus) =>
    status === SampleStatus.Ready || status === SampleStatus.Installing ? CatalogGroupType.Installed : CatalogGroupType.Recommended;

interface SamplesCatalogViewProps {
    onInstall?: (sampleId: string) => void
    onSelection?: (sampleId: string) => void
}

export const SamplesCatalogView = (props: SamplesCatalogViewProps): JSX.Element => {
    const placeholderText = "Sample documents allow you to browse a variety of doc sets from typical document types and use Docugami to learn how to use the products to interact with data.\n\n Click any name in the list to see a more detailed description of the doc set and/or download any you like."

    const { isLoading: catalogLoading } = useGetCatalogSamplesQuery();
    const { isLoading: orgSamplesLoading } = useGetOrganizationSamplesQuery();
    const samples = useAppSelector(state => selectAllSamples(createSelectGetSamplesState()(state)));
    const [install] = useInstallSampleMutation();

    const { search } = useLocation();

    const selectedItemIdFromUrl = useMemo(() => {
        const params = new URLSearchParams(search);
        return params.get('sampleSetId') || undefined;
    }, [search]);

    const [selectedItemId, setSelectedItemId] = useState(selectedItemIdFromUrl || '');

    const groups = useMemo(() => {
        if (!samples?.length) {
            return [];
        }

        const groupedItems: GroupCollection<Sample> = groupBy(samples, x => getCatalogGroupTypeForSampleStatus(x.status));
        return Object.keys(CatalogGroupType).map(groupKey => {
            const group = groupedItems[groupKey] ?? [];
            return ({
                id: groupKey,
                name: groupKey,
                items: group.map((x) => getCatalogItemForSample(x, () => {
                    install(x.id);
                    props.onInstall?.(x.id);
                    Telemetry.trackEvent<InstalledSampleSetSchema>({
                        sampleId: x.id,
                        eventName: TelemetryEvent.InstalledSampleSet
                    });
                }))
            })
        })
    }, [samples, install, props])

    return <Catalog
        onSelectItem={(id) => {
            props.onSelection?.(id);
            setSelectedItemId(id);
        }}
        selectedItemId={selectedItemId}
        title="Sample Library"
        groupItems={groups}
        placeholderText={placeholderText}
        isLoading={catalogLoading || orgSamplesLoading} />
}

function getInstallCommandButton(install: () => void, disabled?: boolean) {
    const className = mergeStyles({
        selectors: {
            '.primaryFill': {
                fill: primaryTheme.palette.black
            }
        }
    });

    return (
        <IconButton
            theme={secondaryTheme}
            className={className}
            data-automation-id={AutomationId.DocExplorerInstallSampleButton}
            title={'Install'}
            disabled={disabled}
            onClick={install}
            iconProps={{
                iconName: dgIconNames[dgIconNames.DgInstall],
                styles: { root: { width: '18px', height: '18px' } }
            }} />
    )
}

interface SampleStatusAndActions {
    statusLabel: string;
    statusIcon: JSX.Element;
    primaryAction?: JSX.Element;
}

function getDetailsForSampleStatus(status: SampleStatus, install: () => void): SampleStatusAndActions {
    const installCommandButton = getInstallCommandButton(
        install,
        status === SampleStatus.Installing || status === SampleStatus.Ready
    );

    switch (status) {
        case SampleStatus.Ready:
            return {
                statusLabel: 'Installed',
                statusIcon: <Badge badgeColor={primaryTheme.palette.themePrimary} />
            };
        case SampleStatus.Uninstalled:
        case SampleStatus.Error:
            return {
                statusLabel: 'Not Installed',
                statusIcon: installCommandButton,
                primaryAction: installCommandButton
            };
        case SampleStatus.Installing:
            return {
                statusLabel: 'Installing',
                statusIcon: <Spinner />,
            };
    }
}

function getCatalogItemForSample(sample: Sample, install: () => void): ICatalogItem {
    const { statusLabel, statusIcon, primaryAction } = getDetailsForSampleStatus(sample.status, install);
    return ({
        id: sample.id,
        name: sample.name,
        statusProps: {
            label: statusLabel,
            icon: statusIcon
        },
        primaryAction: primaryAction,
        description: <PreviewText>{sample.description}</PreviewText>
    })
}
